import { isDefined, schAloftArea } from '@aloft/toolkit-shared';
import { Button, Flex, Grid, SimpleInput, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { useParams } from 'react-router';

import { VacantLandSalesMap } from '../../../components/VacantLandSales/VacantLandSalesMap.tsx';
import { VacantLandSalesTable } from '../../../components/VacantLandSales/VacantLandSalesTable.tsx';
import { SiteExtractionGraph } from '../../../components/graphs/costApproach/SiteExtractionGraph.tsx';
import { SiteSizeVsSiteValueGraph } from '../../../components/graphs/costApproach/SiteSizeVsSiteValueGraph.tsx';
import { SiteToValueRatioGraph } from '../../../components/graphs/costApproach/SiteToValueRatioGraph.tsx';
import { VacantLandCostPerSFVsLotSizeGraph } from '../../../components/graphs/costApproach/VacantLandCostPerSFVsLotSizeGraph.tsx';
import { SubjectVacantLandSalesChartData } from '../../../components/graphs/costApproach/types.ts';
import {
  getVacantLandChartData,
  getVacantLandSalesExponentialFit,
} from '../../../components/graphs/costApproach/utils.ts';
import { useVacantLandSales } from '../../../hooks/useVacantLandSales.tsx';
import { selectEditablePropertyDataById } from '../../../redux/features/selectors/propertyDataSelectors/editablePropertyDataSelectors.ts';
import { selectPropertyIdentificationById } from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSiteCostPerSf } from '../../../redux/features/selectors/selectSiteCostPerSf.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { DistributionOfVacantLandGraph } from './DistributionOfVacantLandGraph.tsx';

const VacantLandSalesMapTable = ({
  vacantLandSales,
}: {
  vacantLandSales: ReturnType<typeof useVacantLandSales>;
}) => {
  const orderID = useParams().orderId!;
  const subjectPropertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const subjectCoordinates = subjectPropertyIdentification?.coordinates;

  const [searchRadius, setSearchRadius] = useState<number>(5);
  const [focusedRow, setFocusedRow] = useState<string | undefined>(undefined);
  const [view, setView] = useState<'map' | 'table'>('table');

  if (!vacantLandSales || !subjectCoordinates) return null;

  const vacantLandSalesWithinRadius = vacantLandSales.filter(
    ({ distanceToSubject }) => distanceToSubject <= searchRadius,
  );

  const focusedPropertyIndex = vacantLandSalesWithinRadius.findIndex(({ id }) => id === focusedRow);
  const focusedPropertyId =
    focusedPropertyIndex !== -1 ? vacantLandSalesWithinRadius[focusedPropertyIndex]?.id : undefined;

  return (
    <Flex
      flexDirection="column"
      gap="1rem"
      height="350px"
      backgroundColor="surface1"
      padding="1rem"
      overflow="hidden"
      borderRadius="8px"
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap="1rem">
          <Typography variant="h3">Vacant Land Sales</Typography>
          <Flex alignItems="center">
            <Typography variant="h3" color="textTertiary">
              Sales Within:&nbsp;
            </Typography>
            <SimpleInput
              type="number"
              width="2rem"
              value={searchRadius.toString()}
              onChange={(e) => setSearchRadius(Number(e.target.value))}
            />
            <Typography variant="h3" color="textTertiary">
              &nbsp;mile(s)
            </Typography>
          </Flex>
        </Flex>
        <Flex alignItems="center" gap="0.5rem">
          <Button
            variant="inlineSecondary"
            onClick={() => setView('table')}
            disabled={view === 'table'}
            text="Table"
          />
          <Button
            variant="inlineSecondary"
            onClick={() => setView('map')}
            disabled={view === 'map'}
            text="Map"
          />
        </Flex>
      </Flex>
      <Flex flexGrow={1} width="100%" height="100%">
        {view === 'map' && (
          <VacantLandSalesMap
            subjectCoordinates={subjectCoordinates}
            vacantLandSales={vacantLandSalesWithinRadius}
            focusedPropertyId={focusedPropertyId}
            setFocusedPropertyId={setFocusedRow}
          />
        )}
        {view === 'table' && (
          <VacantLandSalesTable
            orderID={orderID}
            vacantLandSales={vacantLandSalesWithinRadius}
            focusedRowIndex={focusedPropertyIndex}
            onRowClick={(row) => setFocusedRow(row.id)}
            rowsPerPage={3}
          />
        )}
      </Flex>
    </Flex>
  );
};

export const AppraisifyAnalysisGraphs = () => {
  const vacantLandSales = useVacantLandSales();

  const subjectEditableData = useAppSelector((state) =>
    selectEditablePropertyDataById(state, 'subject'),
  );

  const subjectSiteArea = subjectEditableData?.site?.area;
  const subjectPropertyIdentification = subjectEditableData?.propertyIdentification;

  const subjectCostPerSF = useAppSelector((state) =>
    selectSiteCostPerSf(state, 'subject'),
  )?.toDollarLike();

  const subjectVacantLandSalesDataPoint: SubjectVacantLandSalesChartData | undefined =
    subjectSiteArea && isDefined(subjectCostPerSF)
      ? {
          address: subjectPropertyIdentification?.address?.AddressLineText,
          saleDate: subjectEditableData?.transaction.saleDate,
          siteSize: schAloftArea.parse(subjectSiteArea).getUnitAmount('sqft'),
          subjectCostPerSF,
        }
      : undefined;

  const vacantLandSalesChartData = getVacantLandChartData(vacantLandSales);
  if (!subjectVacantLandSalesDataPoint || !vacantLandSalesChartData) return null;

  const vacantLandSalesExponentialFit = getVacantLandSalesExponentialFit(
    vacantLandSalesChartData,
    subjectVacantLandSalesDataPoint,
  );

  const exponentialFitDataPoints = vacantLandSalesExponentialFit.lineData;

  return (
    <Grid gridTemplateColumns="1fr 1fr" padding="1rem" gap="1rem" backgroundColor="surface2">
      <SiteSizeVsSiteValueGraph disableAnimations={false} />
      <VacantLandCostPerSFVsLotSizeGraph
        disableAnimations={false}
        vacantLandSalesChartData={vacantLandSalesChartData}
        exponentialFitDataPoints={exponentialFitDataPoints}
      />
      <SiteToValueRatioGraph disableAnimations={false} />
      <DistributionOfVacantLandGraph
        disableAnimations={false}
        vacantLandSalesChartData={vacantLandSalesChartData}
      />
      <SiteExtractionGraph
        disableAnimations={false}
        vacantLandSalesExponentialFit={vacantLandSalesExponentialFit}
      />
      <VacantLandSalesMapTable vacantLandSales={vacantLandSales} />
    </Grid>
  );
};
